import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { useTaxonomyService } from '@restworld/data-services';
import { Occupation, OccupationSkill, Variable } from '@restworld/utility-types';

export const useExperienceFieldOptions = ({
  occupation,
  domainInterestId,
  skillId,
  setValue,
  domainInterest,
  isAutoSelectSkill = true,
}: {
  occupation?: string;
  domainInterestId?: string;
  skillId?: string;
  setValue: UseFormSetValue<any>;
  domainInterest: Variable[];
  isAutoSelectSkill?: boolean;
}) => {
  const [occupationOptions, setOccupationOptions] = useState<Occupation[]>([]);
  const [occupationSkillOptions, setOccupationSkillOptions] = useState<OccupationSkill[]>([]);
  const taxonomyService = useTaxonomyService();

  const getOccupationOptions = useCallback(async () => {
    if (!domainInterestId) return;
    try {
      let domainInterestIdCp = '';
      const findIndex = domainInterest.findIndex((v) => v.id === domainInterestId);
      if (findIndex > -1) domainInterestIdCp = String(domainInterest[findIndex]['id']);
      const response = await taxonomyService.indexOccupations(domainInterestIdCp);
      setOccupationOptions(response.data);
    } catch (error) {
      console.error(`error fetching occupation`, error);
    }
  }, [domainInterestId, domainInterest, taxonomyService]);

  const occupationOptionsCp: Variable[] = useMemo(
    () =>
      occupationOptions
        .filter((v) => v.label_it)
        .map((v) => ({
          id: v.id.toString(),
          key: v.id.toString(),
          label: v.label_it.label,
          description: v.label_it.description,
        })),
    [occupationOptions]
  );

  const getOccupationId = useMemo(
    () =>
      occupationOptionsCp.reduce((p, c, i, l) => {
        if (c.id === occupation) return c.key;
        return p;
      }, ''),
    [occupationOptionsCp, occupation]
  );

  const occupationSkillOptionsCp: Variable[] = useMemo(() => {
    return occupationSkillOptions.map((v) => ({
      id: v.skill_id.toString(),
      key: v.skill_id.toString(),
      label: v.skill?.label_it?.label ?? '',
    }));
  }, [occupationSkillOptions]);

  const getOccupationSkills = useCallback(async () => {
    if (!getOccupationId) return;
    try {
      const response = await taxonomyService.indexOccupationSkills(+getOccupationId);
      setOccupationSkillOptions(response.data);
    } catch (error) {
      console.error(error, 'error');
    }
  }, [getOccupationId, taxonomyService]);

  useEffect(() => {
    getOccupationOptions();
  }, [getOccupationOptions]);

  useEffect(() => {
    getOccupationSkills();
  }, [getOccupationSkills]);

  const autoSelectSkill = useCallback(() => {
    if (occupationSkillOptions?.length && isAutoSelectSkill && skillId) {
      const getValuesWithOptional = occupationSkillOptions
        .map((v) => {
          if (!v.optional) return v.skill_id.toString();
          return '';
        })
        .filter(Boolean);
      setValue(skillId, [...getValuesWithOptional]);
    }
  }, [isAutoSelectSkill, occupationSkillOptions, setValue, skillId]);

  useEffect(() => {
    autoSelectSkill();
  }, [autoSelectSkill]);

  const returnV = useMemo(
    () => ({
      occupationOptions: occupationOptionsCp,
      occupationSkillOptions: occupationSkillOptionsCp,
      occupationSkillOptionsOriginal: occupationSkillOptions,
    }),
    [occupationOptionsCp, occupationSkillOptions, occupationSkillOptionsCp]
  );
  return returnV;
};
